import { useState, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Modal,
  IconButton,
  LinearProgress,
  Chip,
  Container,
  Grid,
  Paper,
  InputAdornment,
} from "@mui/material"
import { PlayArrow, Close, ChevronLeft, ChevronRight, School, Search, AccessTime, CheckCircle, Clear } from "@mui/icons-material"
import { apiGetAprenda } from "../../services/apiGetAprenda"
import { VideoPlayer } from "../../components/ui/VideoPlayer"
import { returnEnvironment } from "../../utils"
import { ButtonFL } from "../../components/ui/ButtonFL"
import { useCommunityId } from "../../hooks/useCommunityId"

const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "00:00"
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "fit-content",
    maxWidth: '90%',
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
}

const VideoModal = ({
  open,
  selectedVideo,
  currentIndex,
  totalVideos,
  videos,
  videoDurations,
  calculateProgress,
  updateVideoProgress,
  formatTime,
  onClose,
  onPrevVideo,
  onNextVideo,
}) => {
    const startTime = selectedVideo?.estatisticas?.tempoAssistido >= (videoDurations[currentIndex] || 0) * 0.95
        ? 0
        : selectedVideo?.estatisticas?.tempoAssistido || 0

  return (
    <Modal
      open={open}
      onClose={onClose}
      size='small'
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      <Box sx={modalStyle}>
        {selectedVideo && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                borderBottom: 1,
                borderColor: "divider",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight="semibold" noWrap>
                  {selectedVideo.titulo}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vídeo {currentIndex + 1} de {totalVideos}
                </Typography>
              </Box>
              <IconButton onClick={onClose} color="default">
                <Close />
              </IconButton>
            </Box>

              <VideoPlayer
                key={selectedVideo.videoFile}
                url={selectedVideo.videoFile}
                autoPlay={true}
                defaultMuted={false}
                video={{
                  idVideo: selectedVideo.idVideo,
                  url: selectedVideo.videoFile,
                }}
                idAprenda={selectedVideo.id}
                onTimeUpdate={(video) => {
                  const originalIndex = videos.findIndex((v) => v.titulo === selectedVideo.titulo)
                  updateVideoProgress(originalIndex, video.currentTime, video.duration)
                }}
                startTime={startTime}
              />

            <LinearProgress
              variant="determinate"
              value={calculateProgress(videos.findIndex((v) => v.titulo === selectedVideo.titulo))}
              sx={{
                height: 4,
                "& .MuiLinearProgress-bar": {
                  bgcolor: "var(--primary-color)",
                },
              }}
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                bgcolor: "var(--grey-50-color)",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
            <Box>

                <ButtonFL
                    variant="outlined"
                    startIcon={<ChevronLeft />}
                    onClick={onPrevVideo}
                    disabled={currentIndex === 0}
                >
                    Anterior
                </ButtonFL>
            </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const originalIndex = videos.findIndex((v) => v.titulo === selectedVideo.titulo)
                    return `${formatTime(videos[originalIndex]?.estatisticas?.tempoAssistido || 0)} / ${formatTime(videoDurations[originalIndex] || 0)}`
                  })()}
                </Typography>
              </Box>

                <Box>
                    <ButtonFL
                        variant="contained"
                        endIcon={<ChevronRight />}
                        onClick={onNextVideo}
                        disabled={currentIndex === totalVideos - 1}
                        sx={{
                        bgcolor: "var(--primary-color)",
                        "&:hover": {
                            bgcolor: "var(--primary-dark-color)",
                        },
                        }}
                    >
                        Próximo
                    </ButtonFL>
                </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  )
}

export const Learn = () => {
  const [videos, setVideos] = useState([])
  const [filteredVideos, setFilteredVideos] = useState([])
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [searchTerm, setSearchTerm] = useState("")
  const [videoDurations, setVideoDurations] = useState({})
  const [isLoading, setIsLoading] = useState(true)

  const communityId = useCommunityId();

  useEffect(() => {
    const fetchVideos = async () => {
      setIsLoading(true)

        try {
            const environment = await returnEnvironment()

            const data = await apiGetAprenda(environment !== "default" && communityId)

            const videosWithFile = data.filter(video => video.videoFile)

            setVideos(videosWithFile)
            setFilteredVideos(videosWithFile)
        } catch (error) {
            console.error("Erro ao buscar vídeos:", error)
            setVideos([])
            setFilteredVideos([])
        } finally {
            setIsLoading(false)
        }
    }

    fetchVideos()
  }, [])

  useEffect(() => {
    const filtered = videos
      .filter((video) => video.titulo.toLowerCase().includes(searchTerm.toLowerCase()))
    setFilteredVideos(filtered)
  }, [searchTerm, videos])

  const calculateProgress = (videoIndex) => {
    const duration = videoDurations[videoIndex] || 0
    const watched = videos[videoIndex]?.estatisticas?.tempoAssistido || 0
    const progress = (watched / duration) * 100

    if (progress > 100) {
      return 100
    }
    if (progress < 0) {
      return 0
    }
    
    return duration > 0 ? (watched / duration) * 100 : 0
  }

  const isVideoCompleted = (videoIndex) => {
    const duration = videoDurations[videoIndex] || 0
    const watched = videos[videoIndex]?.estatisticas?.tempoAssistido || 0
    return duration > 0 && watched >= duration - (duration * 0.05)
  }

  const openVideo = (video, index) => {
    setSelectedVideo(video)
    setCurrentIndex(index)
  }

  const closeVideo = () => {
    setSelectedVideo(null)
  }

  const nextVideo = () => {
    if (currentIndex < filteredVideos.length - 1) {
      const nextIndex = currentIndex + 1
      setCurrentIndex(nextIndex)
      setSelectedVideo(filteredVideos[nextIndex])
    }
  }

  const prevVideo = () => {
    if (currentIndex > 0) {
      const prevIndex = currentIndex - 1
      setCurrentIndex(prevIndex)
      setSelectedVideo(filteredVideos[prevIndex])
    }
  }

  const updateVideoProgress = (videoIndex, currentTime) => {
    const normalizedCurrentTime = Math.max(0, Math.min(currentTime, videoDurations[videoIndex] || 0))

    setVideos(prev => prev.map((video, i) => 
      i === videoIndex ? { 
        ...video, 
        estatisticas: { 
          ...video.estatisticas, 
          tempoAssistido: normalizedCurrentTime, 
        } 
      } : video
    ))
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "var(--grey-50-color)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              border: "4px solid var(--grey-300-color)",
              borderTop: "4px solid var(--primary-color)",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              mx: "auto",
              mb: 2,
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography variant="h6" color="text.secondary">
            Carregando vídeos...
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "var(--grey-50-color)" }}>
      {/* Header */}
      <Paper elevation={1} sx={{ borderRadius: 0 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: "var(--primary-color)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <School sx={{ color: "white", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold" color="text.primary">
                Aprenda a criar vídeos incríveis
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Descubra dicas e técnicas para produzir conteúdo de alta qualidade com nossos vídeos passo a passo.
              </Typography>
            </Box>
          </Box>

          {/* Search Bar */}
          <TextField
                margin="normal"
                id="search"
                label="Buscar vídeos"
                name="search"
                autoComplete="search"
                value={searchTerm}
                fullWidth
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                        setSearchTerm(e.target.value);
                    }
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            {searchTerm && (
                                <IconButton
                                    aria-label="Limpar busca"
                                    size="small"
                                    onClick={() => {
                                        setSearchTerm('');
                                    }}
                                >
                                    <Clear />
                                </IconButton>
                            )}
                            <IconButton aria-label="Buscar" size="small" onClick={() => setSearchTerm(searchTerm)}>
                                <Search />
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
        </Container>
      </Paper>

      {/* Videos Grid */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {filteredVideos.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <PlayArrow sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
            <Typography variant="h6" fontWeight="medium" color="text.primary" gutterBottom>
              Nenhum vídeo encontrado
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Tente buscar com outros termos
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredVideos.map((video, index) => {
              if (!video.videoFile) {
                return null
              }

              const originalIndex = videos.findIndex((v) => v.titulo === video.titulo)
              const duration = videoDurations[originalIndex] || 0
              const watched = videos[originalIndex]?.estatisticas?.tempoAssistido || 0
              const normalizedWatched = Math.max(0, Math.min(watched, duration))
              const progress = calculateProgress(originalIndex)
              const isCompleted = isVideoCompleted(originalIndex)

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={originalIndex}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 4,
                      },
                    }}
                    onClick={() => openVideo(video, index)}
                  >
                    <Box sx={{ position: "relative" }}>
                      <video
                        src={video.videoFile}
                        muted
                        preload="metadata"
                        controls={false}
                        autoPlay={false}
                        loop={false}
                        style={{
                          width: "100%",
                          height: "180px",
                          objectFit: "cover",
                          bgcolor: "var(--grey-100-color)",
                          cursor: "pointer",
                          pointerEvents: "none",
                          display: "block",
                        }}
                        onLoadedMetadata={(e) => {
                          setVideoDurations((prev) => ({
                            ...prev,
                            [originalIndex]: e.target.duration,
                          }))
                            setTimeout(() => e.target.currentTime = 2, 500)
                        }}
                        onTimeUpdate={(e) => {
                          if (!e.target.paused) {
                            e.target.pause()
                          }
                        }}
                      />

                      {/* Badge de Completado */}
                      {isCompleted && (
                        <Chip
                          icon={<CheckCircle />}
                          label="Concluído"
                          color="success"
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            fontWeight: "medium",
                          }}
                        />
                      )}

                      {/* Duração do Vídeo */}
                      <Chip
                        label={duration > 0 ? formatTime(duration) : "--:--"}
                        size="small"
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          bgcolor: "rgba(0,0,0,0.8)",
                          color: "white",
                          fontSize: "0.75rem",
                        }}
                      />

                      {/* Progress Bar */}
                      {progress > 0 && (
                        <LinearProgress
                          variant="determinate"
                          value={progress}
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: 6,
                            "& .MuiLinearProgress-bar": {
                              bgcolor: "var(--primary-light-color)",
                            },
                          }}
                        />
                      )}

                      {/* Play Overlay */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "rgba(0,0,0,0)",
                          transition: "all 0.2s",
                          "&:hover": {
                            bgcolor: "rgba(0,0,0,0.2)",
                            "& .play-button": {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        <Box
                          className="play-button"
                          sx={{
                            width: 64,
                            height: 64,
                            bgcolor: "rgba(255,255,255,0.9)",
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            opacity: 0,
                            transition: "opacity 0.2s",
                            boxShadow: 2,
                          }}
                        >
                          <PlayArrow sx={{ color: "var(--primary-color)", fontSize: 32, ml: 0.5 }} />
                        </Box>
                      </Box>
                    </Box>

                    <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="medium"
                        color={progress > 0 ? "var(--primary-color)" : "text.primary"}
                        sx={{
                          mb: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          flexGrow: 1,
                        }}
                      >
                        {`${index + 1} - ${video.titulo}`}
                      </Typography>

                      {/* Progresso e Tempo */}
                      {duration > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <AccessTime sx={{ fontSize: 16, color: "text.secondary" }} />
                            <Typography variant="caption" color="text.secondary">
                              {formatTime(normalizedWatched)} / {formatTime(duration)}
                            </Typography>
                          </Box>
                          {progress > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {Math.round(progress)}%
                            </Typography>
                          )}
                        </Box>
                      )}

                      <ButtonFL
                        variant="outlined"
                        fullWidth
                        startIcon={<PlayArrow />}
                        sx={{
                          color: "var(--primary-color)",
                          borderColor: "var(--primary-light-color)",
                          borderWidth: 2,
                          "&:hover": {
                            borderColor: "var(--primary-light-color)",
                            bgcolor: "var(--primary-10-color)",
                            borderWidth: 2,
                          },
                        }}
                      >
                        {progress >= 99 ? "Assistir novamente" : progress > 0 ? "Continuar" : "Assistir"}
                      </ButtonFL>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        )}
      </Container>

      <VideoModal
        open={!!selectedVideo}
        selectedVideo={selectedVideo}
        currentIndex={currentIndex}
        totalVideos={filteredVideos.length}
        videos={videos}
        videoDurations={videoDurations}
        calculateProgress={calculateProgress}
        updateVideoProgress={updateVideoProgress}
        formatTime={formatTime}
        onClose={closeVideo}
        onPrevVideo={prevVideo}
        onNextVideo={nextVideo}
      />
    </Box>
  )
}
